import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import { isNil } from 'lodash'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { Button, Dialog, DialogActions, DialogContent, Grid, Box } from '@mui/material'
import { CardTable } from '../../../../../components/datatable/NewTable'
import { darkBlue } from '../../../../../utils/constants/ColorTheme'
import { DefaultDialogTitle } from '../../../../../components/styled/Dialog'
import Select from '../../../../../components/forms/Select'
import useSandreList from '../../../../../utils/customHook/useSandreList'
import TaxonAction from '../../actions/TaxonAction'
import { ecobioTaxonTraitShape } from '../../dto/EcobioTaxonTraitShape'

const TAXONS_GROUPE = 'TAXONS.GROUPE'

const EcoFriendlyFeatures = ({ taxon, taxonTraits, onAdd, onDelete, disabled }) => {
    const dispatch = useDispatch()

    const {
        ecobioTraits,
        ecobioModalities,
    } = useSelector(store => ({
        ecobioTraits: store.TaxonReducer.ecobioTraits,
        ecobioModalities: store.TaxonReducer.ecobioModalities,
    }), shallowEqual)

    const taxonsGroupe = useSandreList(TAXONS_GROUPE)

    const [open, setOpen] = useState(false)
    const [group, setGroup] = useState(undefined)
    const [trait, setTrait] = useState(undefined)
    const [modality, setModality] = useState(undefined)

    useEffect(() => {
        dispatch(TaxonAction.fetchEcobioTraits())
        dispatch(TaxonAction.fetchEcobioModalities())
    }, [dispatch])

    const filteredTraits = useMemo(
        () => (isNil(group) ? [] : ecobioTraits.filter(t => Number(t.groupCode) === Number(group))),
        [ecobioTraits, group],
    )

    const filteredModalities = useMemo(
        () => (isNil(trait) ? [] : ecobioModalities.filter(m => Number(m.traitCode) === Number(trait))),
        [ecobioModalities, trait],
    )

    const resetForm = () => {
        setGroup(undefined)
        setTrait(undefined)
        setModality(undefined)
    }

    const onClose = () => {
        setOpen(false)
        resetForm()
    }

    const onValidate = () => {
        onAdd({
            traitCode: trait,
            modalityCode: modality,
            taxonCode: taxon.code,
        })
        onClose()
    }

    const tableRows = useMemo(() => taxonTraits.map(association => {
        const associatedTrait = ecobioTraits.find(t => Number(t.code) === Number(association.traitCode))
        const associatedGroup = taxonsGroupe.find(g => Number(g.code) === Number(associatedTrait?.groupCode))
        const associatedModality = ecobioModalities.find(m => Number(m.traitCode) === Number(association.traitCode) && Number(m.code) === Number(association.modalityCode))
        return {
            group: associatedGroup?.name,
            trait: associatedTrait?.name,
            modality: associatedModality?.name,
            association,
        }
    }), [taxonTraits, ecobioTraits, ecobioModalities, taxonsGroupe])

    const headers = [
        { key: 'group', value: i18n.group },
        { key: 'trait', value: i18n.trait },
        { key: 'modality', value: i18n.modality },
    ]

    return (
        <>
            <CardTable
                title={i18n.ecoFriendlyFeatures}
                rows={tableRows}
                headers={headers}
                lineActions={disabled ? [] : [{
                    icon: 'delete',
                    tooltip: i18n.delete,
                    onClick: row => onDelete(row.association),
                }]}
                actions={disabled ? [] : [{ icon: 'note_add', tooltip: i18n.add, onClick: () => setOpen(true), color: darkBlue }]}
            />

            <Dialog open={open} onClose={onClose} maxWidth='lg' fullWidth>
                <DefaultDialogTitle title={i18n.ecoFriendlyFeatures} onClose={onClose} />
                <DialogContent>
                    <Grid container spacing={1}>
                        <Grid size={4}>
                            <Select
                                label={i18n.group}
                                value={group}
                                options={taxonsGroupe}
                                onChange={value => {
                                    setGroup(value)
                                    setTrait(undefined)
                                    setModality(undefined)
                                }}
                            />
                        </Grid>
                        <Grid size={4}>
                            {!group ? (
                                <Select
                                    label={i18n.trait}
                                    value={trait}
                                    options={[]}
                                    disabled
                                    onChange={() => {}}
                                />
                            ) : filteredTraits.length === 0 ? (
                                <Box
                                    sx={{
                                        height: 50,
                                        display: 'flex',
                                        alignItems: 'center',
                                        color: 'text.secondary',
                                    }}
                                >
                                    {i18n.noMatchingTraitAssociatedToGroup}
                                </Box>
                            ) : (
                                <Select
                                    label={i18n.trait}
                                    value={trait}
                                    options={filteredTraits}
                                    onChange={value => {
                                        setTrait(value)
                                        setModality(undefined)
                                    }}
                                />
                            )}
                        </Grid>
                        <Grid size={4}>
                            {!trait ? (
                                <Select
                                    label={i18n.modality}
                                    value={modality}
                                    options={[]}
                                    disabled
                                    onChange={() => {}}
                                />
                            ) : filteredModalities.length === 0 ? (
                                <Box
                                    sx={{
                                        height: 50,
                                        display: 'flex',
                                        alignItems: 'center',
                                        color: 'text.secondary',
                                    }}
                                >
                                    {i18n.noMatchingModalityAssociatedToTrait}
                                </Box>
                            ) : (
                                <Select
                                    label={i18n.modality}
                                    value={modality}
                                    options={filteredModalities}
                                    onChange={value => setModality(value)}
                                />
                            )}
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant='contained'
                        color='primary'
                        onClick={onValidate}
                        disabled={isNil(group) || isNil(trait) || isNil(modality)}
                    >
                        {i18n.validate}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

EcoFriendlyFeatures.propTypes = {
    taxon: PropTypes.shape({
        code: PropTypes.string,
    }),
    taxonTraits: PropTypes.arrayOf(ecobioTaxonTraitShape),
    onAdd: PropTypes.func,
    onDelete: PropTypes.func,
    disabled: PropTypes.bool,
}

export default EcoFriendlyFeatures